import * as signalR from "@microsoft/signalr";
import { AppInsightsLogger } from "../components/shared/logging/AppInsightsLogger";
import { millisecondsUntil } from "../helpers/auctionDateTime";
import IPublicParcelState from "../models/auction/IPublicParcelState";
import AuctionsHub, {
    IAdminEnBlocApprovedBidders,
    IApprovedParcelBid,
    IBatchLiveUpdate,
    IBidderEnBlocApproval,
    IBidProcessingComplete,
    ILatencyTestResults,
    IParcelBidLogUpdate,
    IParcelClocksUpdate
} from "../models/hubs/AuctionsHub";
import { IParcelBid } from "../models/parcel/IParcelBid";
import { BidLoadState } from "../models/utility/LoadState";
import { IAppDispatch, IAppState } from "../store";
import * as AuctionStore from "../store/AuctionStore";
import { actionCreators as ParcelBidActions } from "../store/ParcelBidStore";
import * as ParcelBidStore from "../store/ParcelBidStore";
import TokenProvider from "./TokenProvider";

enum ConnectionAttemptState {
    None,
    ShouldConnect,
    ShouldDisconnect
}

const MAX_RETRY_ELAPSED_MS = 60 * 1000; // 60 seconds

export default class SignalRProvider {
    public static AuctionHubConnection: signalR.HubConnection;

    private static logMissedMessagesId: NodeJS.Timeout;
    private static currentConnectionAttempt = ConnectionAttemptState.None;

    public static BuildConnections(
        dispatch: IAppDispatch,
        getState: () => IAppState,
        hubConnectionBuilder: signalR.HubConnectionBuilder = new signalR.HubConnectionBuilder()
    ): void {
        this.AuctionHubConnection = hubConnectionBuilder
            .withUrl("/hub/Auctions", {
                accessTokenFactory: () => TokenProvider.GetStoredToken() || ""
            })
            .configureLogging(signalR.LogLevel.Information)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    console.log(
                        `SignalR reconnect attempt #${retryContext.previousRetryCount}`
                    );

                    if (
                        retryContext.elapsedMilliseconds < MAX_RETRY_ELAPSED_MS
                    ) {
                        // If we've been reconnecting for less than 60 seconds so far,
                        // wait between 0 and 10 seconds before the next reconnect attempt.
                        // Prefer a shorter time, so we can display the "disconnected" alert
                        return Math.random() * 10000;
                    } else {
                        const message = `SignalR reconnect failed after ${retryContext.previousRetryCount} attempts.`;
                        console.error(message);
                        AppInsightsLogger.trackEvent({ name: message });
                        return null;
                    }
                }
            })
            .build();

        this.AuctionHubConnection.onreconnecting(() => {
            AppInsightsLogger.trackEvent({
                name: "SignalR is attempting to reconnect"
            });
        });

        this.AuctionHubConnection.onreconnected(() => {
            AppInsightsLogger.trackEvent({
                name: "SignalR successfully reconnected"
            });

            AuctionStore.actionCreators.getParcelStates()(dispatch, getState);
        });

        this.AuctionHubConnection.onclose((err?: Error) => {
            const networkOnline = navigator.onLine;
            const connectionState = this.AuctionHubConnection.state;
            const errorDetails = err
                ? `${err.name}: ${err.message}`
                : "No error details";

            const properties = {
                errorDetails,
                networkOnline: String(networkOnline),
                connectionState,
                visibilityState: document.hidden ? "hidden" : "visible",
                timestamp: new Date().toISOString()
            };

            AppInsightsLogger.trackEvent({
                name: `SignalR connection closed`,
                properties
            });

            console.log(
                `SignalR connection closed. ${JSON.stringify(
                    properties,
                    null,
                    2
                )}`
            );
        });

        // register commands for all built connections
        this.registerReceiveCommands(dispatch, getState);
        this.StartAllConnections(dispatch, getState);
    }

    public static async ResetAllConnections(
        dispatch: IAppDispatch,
        getState: () => IAppState
    ): Promise<void> {
        await this.StopAllConnections(dispatch, getState);
        await this.StartAllConnections(dispatch, getState);
    }

    public static GetState = () => this.AuctionHubConnection.state;

    private static async StartAllConnections(
        dispatch: IAppDispatch,
        getState: () => IAppState
    ): Promise<void> {
        // If another attempt is already in flight, note attempt type & don't start connection
        const isAlreadyInProgress =
            this.currentConnectionAttempt !== ConnectionAttemptState.None;
        this.currentConnectionAttempt = ConnectionAttemptState.ShouldConnect;
        if (isAlreadyInProgress) {
            return;
        }

        // Otherwise, connect
        try {
            await this.AuctionHubConnection.start();
            AppInsightsLogger.trackEvent({
                name: `SignalR connection started.`
            });
            AuctionStore.actionCreators.getParcelStates()(dispatch, getState);
        } catch (err) {
            let msg = "";
            // parse error message
            if (err instanceof Error) {
                msg = err.message;
                if (!msg) {
                    msg = "Connection refused.";
                }
            } else if (typeof err === "string") {
                msg = err;
            } else {
                msg = JSON.stringify(err);
            }
            AppInsightsLogger.trackEvent({
                name: `Error: SignalR could not connect (${
                    document.hidden ? "Not visible" : "Visible"
                }): ${msg}`
            });
        } finally {
            // After connection is started or fails, check if connection attempt type changed. Could've changed multiple times,
            // but we only care if disconnection attempted during connection & connection succeeded
            const disconnectAttemptedDuringConnection =
                this.isCurrentConnectionAttempt(
                    ConnectionAttemptState.ShouldDisconnect
                );
            this.currentConnectionAttempt = ConnectionAttemptState.None;
            if (
                disconnectAttemptedDuringConnection &&
                this.AuctionHubConnection.state ===
                    signalR.HubConnectionState.Connected
            ) {
                this.StopAllConnections(dispatch, getState);
            }
        }
    }

    private static isCurrentConnectionAttempt(
        stateToCheck: ConnectionAttemptState
    ): boolean {
        // Stephen Visser, Oct 27 -- prefer to do this inline but moved into method to address compile-time complaint
        return this.currentConnectionAttempt === stateToCheck;
    }

    private static async StopAllConnections(
        dispatch: IAppDispatch,
        getState: () => IAppState
    ): Promise<void> {
        // If another attempt is already in flight, just note our attempt type & don't disconnect
        const isAlreadyInProgress =
            this.currentConnectionAttempt !== ConnectionAttemptState.None;
        this.currentConnectionAttempt = ConnectionAttemptState.ShouldDisconnect;
        if (isAlreadyInProgress) {
            return;
        }

        SignalRProvider.clearTimeoutForMissedMessages();

        // Otherwise disconnect
        try {
            await this.AuctionHubConnection.stop();
        } catch (err) {
            let msg = "";
            // parse error message
            if (err instanceof Error) {
                msg = err.message;
                if (!msg) {
                    msg = "Connection refused.";
                }
            } else if (typeof err === "string") {
                msg = err;
            } else {
                msg = JSON.stringify(err);
            }
            AppInsightsLogger.trackEvent({
                name: `Error: SignalR failed to disconnect. ${msg}`
            });
        } finally {
            // After disconnection is started, check if connection attempt type changed. Could've changed multiple times,
            // but we only care if connection attempt made during disconnection & disconnection succeeded.
            const connectAttemptedDuringDisconnection =
                this.isCurrentConnectionAttempt(
                    ConnectionAttemptState.ShouldConnect
                );
            this.currentConnectionAttempt = ConnectionAttemptState.None;
            if (
                connectAttemptedDuringDisconnection &&
                signalR.HubConnectionState.Disconnected
            ) {
                this.StartAllConnections(dispatch, getState);
            }
        }
    }

    private static setTimeoutForMissedMessages = (messageDueTime: string) => {
        SignalRProvider.clearTimeoutForMissedMessages();

        const timeout = millisecondsUntil(messageDueTime);
        SignalRProvider.logMissedMessagesId = setTimeout(
            SignalRProvider.LogMissedMessages,
            timeout
        );
    };

    private static clearTimeoutForMissedMessages = () => {
        clearTimeout(SignalRProvider.logMissedMessagesId);
    };

    private static LogMissedMessages = () => {
        AppInsightsLogger.trackEvent({
            name: "SignalR - ReceiveParcelClocksUpdate message missed"
        });
    };

    private static registerReceiveCommands(
        dispatch: IAppDispatch,
        getState: () => IAppState
    ) {
        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveBatchLiveStatus,
            (update: IBatchLiveUpdate) => {
                update.auctions.forEach((a) => {
                    dispatch({
                        type: "TOGGLE_AUCTION_LIVE_STATUS",
                        auctionId: a.auctionId,
                        live: a.live,
                        completed: a.completed
                    });
                });
                update.parcels.forEach((p) => {
                    dispatch({
                        endDate: p.endDate,
                        isCompleted: p.isCompleted,
                        isLive: p.isLive,
                        parcelId: p.parcelId,
                        startDate: p.startDate,
                        type: "TOGGLE_PARCEL_LIVE_STATUS"
                    });
                });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveApprovedParcelBid,
            (update: IApprovedParcelBid) => {
                const { bidDialogVisible, currentBid } =
                    getState().parcelBidStoreState;

                // Any accepted bid makes the open dialog's amount stale, even the
                // bidder's own from another tab, so close it to avoid an outbid error.
                if (
                    bidDialogVisible &&
                    currentBid.submitLoadState === BidLoadState.NotSubmitted &&
                    update.parcelId === currentBid.parcelId
                ) {
                    ParcelBidStore.actionCreators.resetCurrentBid()(
                        dispatch,
                        getState
                    );
                    ParcelBidStore.actionCreators.toggleDialogVisible(false)(
                        dispatch,
                        getState
                    );
                }

                dispatch({
                    type: "UPDATE_PARCEL_BID_AMOUNT",
                    amount: update.bidAmount,
                    parcelId: update.parcelId,
                    tokenId: update.checkoutToken,
                    endDate: update.endTime
                });

                const bid: IParcelBid = {
                    parcelBidId: update.parcelBidId,
                    bidAmount: update.bidAmount,
                    bidReceivedTime: update.bidReceivedTime,
                    parcelId: update.parcelId
                };
                dispatch({ type: "ADD_PARCEL_BID", bid });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveParcelBidLogUpdate,
            (update: IParcelBidLogUpdate) => {
                dispatch({
                    type: "ADD_PARCEL_ADMIN_BID_LOG",
                    parcelId: update.parcelId,
                    bidLog: update.bidLog
                });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveParcelClocksUpdate,
            (update: IParcelClocksUpdate) => {
                SignalRProvider.setTimeoutForMissedMessages(
                    update.nextMessageDueTime
                );

                let connectionShouldReset = false;

                const resetConnection = () => {
                    connectionShouldReset = true;
                };

                dispatch({
                    type: "CHECKPOINT_LIVE_PARCELS",
                    liveParcels: update.liveParcels,
                    resetConnection
                });

                if (connectionShouldReset) {
                    AppInsightsLogger.trackEvent({
                        name: "Client out of sync, reconnecting..."
                    });
                    console.log("Client out of sync, reconnecting...");
                    SignalRProvider.ResetAllConnections(dispatch, getState);
                }

                const offset = millisecondsUntil(update.currentServerTime);
                dispatch({ type: "SET_CLIENT_TIMER_OFFSET", offset });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceivePublicParcelState,
            (update: IPublicParcelState) => {
                dispatch({
                    type: "APPLY_PARCEL_STATE",
                    parcelState: update
                });
                if (update.updateMessage) {
                    dispatch({
                        type: "SHOW_PARCEL_UPDATED_SNACKBAR_MESSAGE",
                        auctionId: update.auctionId,
                        message: update.updateMessage
                    });
                    dispatch({ type: "HIDE_PARCEL_UPDATED_SNACKBAR_MESSAGE" });
                }
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveBidderEnBlocApproval,
            (update: IBidderEnBlocApproval) => {
                dispatch({
                    type: "APPLY_BIDDER_EN_BLOC_APPROVAL",
                    parcelId: update.parcelId,
                    isApproved: update.isApproved
                });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveAdminEnBlocApprovedBidders,
            (update: IAdminEnBlocApprovedBidders) => {
                dispatch({
                    type: "APPLY_ADMIN_EN_BLOC_APPROVED_BIDDERS",
                    parcelId: update.parcelId,
                    approvedBidderIds: update.approvedBidderIds
                });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.DebugCommands.ReceiveLatencyTestResults,
            (results: ILatencyTestResults) => {
                dispatch({
                    testResults: results,
                    type: "RECEIVE_LATENCY_TEST_RESULTS"
                });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.BidProcessingComplete,
            (bidResult: IBidProcessingComplete) => {
                if (
                    bidResult.result === BidLoadState.Success &&
                    bidResult.parcelBidId
                ) {
                    dispatch({
                        type: "ADD_MY_BID_ID",
                        bidId: bidResult.parcelBidId
                    });
                }

                // The result reaches every tab the bidder has open. Only the tab
                // that submitted the bid shows it; another tab's dialog is left
                // for the accepted-bid broadcast to close.
                const { currentBid } = getState().parcelBidStoreState;
                if (
                    currentBid.submitLoadState !== BidLoadState.Submitting ||
                    currentBid.parcelId !== bidResult.parcelId
                ) {
                    return;
                }

                dispatch({
                    type: "TOGGLE_BID_LOAD_STATE",
                    errorText: bidResult.errorText,
                    state: bidResult.result
                });

                ParcelBidActions.updateCurrentBid(
                    bidResult.parcelId,
                    bidResult.result,
                    bidResult.checkoutToken,
                    bidResult.lastAcceptedBid,
                    bidResult.errorText
                )(dispatch, getState);
            }
        );
    }
}
